/**
 * ABDP validation is synced in pages of ABDP_SYNC_FOLDER_CHUNK folders (250 by
 * default). The backend reports progress on each `validate` response as
 * `validate_total` / `validate_synced` / `validate_next_offset`; while
 * `validate_next_offset` is non-zero another page is still pending.
 *
 * Before this helper the user had to click "Run validation" once per page and
 * read the "Run validation again for the next set" hint, so a 1,000-loan batch
 * looked like it only had ~250 loans' rules. This keeps calling until the last
 * page is in, keeping each HTTP request short enough for the ingress timeout.
 */

export const VALIDATE_MAX_ROUNDS = 400;

export function validateProgressMessage(result) {
  const total = Number(result?.validate_total);
  const synced = Number(result?.validate_synced);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(synced)) {
    return 'Syncing validation rules from ABDP…';
  }
  return `Syncing validation rules from ABDP… ${Math.min(synced, total)} of ${total} loan folder(s) done.`;
}

export function validateHasMorePages(result) {
  const next = Number(result?.validate_next_offset);
  return Number.isFinite(next) && next > 0;
}

/**
 * @param {() => Promise<object>} runOnce - calls the validate action once.
 * @param {{ onProgress?: (result: object, round: number) => void, maxRounds?: number }} [options]
 * @returns {Promise<object>} the final validate response, with `message`
 *   rewritten to summarise the full run when more than one page was synced.
 */
export async function runValidateToCompletion(runOnce, options = {}) {
  const { onProgress, maxRounds = VALIDATE_MAX_ROUNDS } = options;
  let result = await runOnce();
  let round = 1;
  let rulesWritten = Number(result?.validation_written) || 0;
  let lastOffset = Number(result?.validate_next_offset) || 0;

  while (validateHasMorePages(result) && round < maxRounds) {
    if (typeof onProgress === 'function') onProgress(result, round);
    // eslint-disable-next-line no-await-in-loop
    const next = await runOnce();
    round += 1;
    rulesWritten += Number(next?.validation_written) || 0;
    const nextOffset = Number(next?.validate_next_offset) || 0;
    result = next;
    if (nextOffset !== 0 && nextOffset === lastOffset) {
      // The server did not advance; stop rather than loop on the same page.
      break;
    }
    lastOffset = nextOffset;
  }

  if (round > 1 && result) {
    const total = Number(result.validate_total);
    const suffix = Number.isFinite(total) && total > 0 ? ` across ${total} loan folder(s)` : '';
    const stillPending = validateHasMorePages(result)
      ? ' Some folders are still pending — run validation again to continue.'
      : '';
    return {
      ...result,
      validation_written: rulesWritten,
      message: `ABDP validation synced: ${rulesWritten} rule(s)${suffix} in ${round} page(s).${stillPending}`,
    };
  }
  return result;
}
